import { siteConfig } from '../data/siteConfig';

/**
 * Google Places Autocomplete (New) adapter — Maps JavaScript API, `places` library.
 * Active ONLY when siteConfig.mapsBrowserKey is set (a browser key restricted by HTTP referrer
 * to tampa.instamoversfl.com and to the Maps JavaScript + Places APIs). Without a key the UI falls
 * back to honest manual entry. No local/fake address lists.
 */

export interface PlaceSuggestion {
  id: string;
  main: string;
  secondary: string;
  /** Opaque handle used to fetch details for this suggestion. */
  ref: unknown;
}

export interface PlaceAddress {
  street: string;
  city: string;
  state: string;
  zip: string;
  placeId: string;
}

interface AddressComponent {
  longText: string | null;
  shortText: string | null;
  types: string[];
}

interface PlaceLike {
  id: string;
  addressComponents?: AddressComponent[];
  fetchFields: (o: { fields: string[] }) => Promise<unknown>;
}

interface PlacePredictionLike {
  placeId: string;
  mainText?: { text: string } | null;
  secondaryText?: { text: string } | null;
  text?: { text: string };
  toPlace: () => PlaceLike;
}

interface PlacesLibrary {
  AutocompleteSuggestion: {
    fetchAutocompleteSuggestions: (req: Record<string, unknown>) => Promise<{ suggestions: { placePrediction: PlacePredictionLike | null }[] }>;
  };
  AutocompleteSessionToken: new () => unknown;
}

interface GoogleGlobal {
  maps: { importLibrary: (name: string) => Promise<unknown> };
}

declare global {
  interface Window {
    google?: GoogleGlobal;
    __imfMapsLoading?: Promise<void>;
  }
}

/** Tampa Bay bias. Not a restriction — out-of-area addresses still appear and go to team review. */
const TAMPA = { lat: 27.9506, lng: -82.4572 };

export function placesEnabled(): boolean {
  return !!siteConfig.mapsBrowserKey;
}

function loadMaps(): Promise<void> {
  if (window.google?.maps?.importLibrary) return Promise.resolve();
  if (window.__imfMapsLoading) return window.__imfMapsLoading;
  window.__imfMapsLoading = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(siteConfig.mapsBrowserKey ?? '')}&v=weekly&loading=async&libraries=places`;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('maps_load_failed'));
    document.head.appendChild(s);
  });
  return window.__imfMapsLoading;
}

let lib: PlacesLibrary | null = null;
async function places(): Promise<PlacesLibrary> {
  if (lib) return lib;
  await loadMaps();
  // importLibrary may resolve a moment after script onload.
  for (let i = 0; i < 20 && !window.google?.maps?.importLibrary; i++) await new Promise((r) => setTimeout(r, 50));
  if (!window.google?.maps?.importLibrary) throw new Error('maps_unavailable');
  lib = (await window.google.maps.importLibrary('places')) as PlacesLibrary;
  return lib;
}

/** One session token per search (from first keystroke to selection) for correct Places billing. */
export async function newSessionToken(): Promise<unknown> {
  const l = await places();
  return new l.AutocompleteSessionToken();
}

export async function fetchSuggestions(input: string, sessionToken: unknown): Promise<PlaceSuggestion[]> {
  const l = await places();
  const { suggestions } = await l.AutocompleteSuggestion.fetchAutocompleteSuggestions({
    input,
    sessionToken,
    includedRegionCodes: ['us'],
    locationBias: { center: TAMPA, radius: 50000 },
    language: document.documentElement.lang || 'en',
  });
  return suggestions
    .map((s) => s.placePrediction)
    .filter((p): p is PlacePredictionLike => !!p)
    .map((p) => ({ id: p.placeId, main: p.mainText?.text ?? p.text?.text ?? '', secondary: p.secondaryText?.text ?? '', ref: p }));
}

/** Place Details: fill fields from address components, never by splitting the display text on commas. */
export async function fetchAddress(s: PlaceSuggestion): Promise<PlaceAddress> {
  const place = (s.ref as PlacePredictionLike).toPlace();
  await place.fetchFields({ fields: ['addressComponents'] });
  const comps = place.addressComponents ?? [];
  const get = (type: string, short = false) => {
    const c = comps.find((x) => x.types.includes(type));
    return (short ? c?.shortText : c?.longText) ?? '';
  };
  const street = [get('street_number'), get('route')].filter(Boolean).join(' ');
  const city = get('locality') || get('sublocality') || get('postal_town') || get('administrative_area_level_3');
  return { street, city, state: get('administrative_area_level_1', true), zip: get('postal_code'), placeId: place.id };
}
